<template>
  <v-container>
    <h3>デジタル入力チャンネル使用設定</h3>

    <v-form @submit.prevent ref="form" class="mt-8 mb-5">

      <v-row>
        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center">
          <v-label>チャンネル選択</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 8">
          <v-select density="compact" hide-details="true"
            v-model:model-value="channelKey" :items="channels"></v-select>
        </v-col>
      </v-row>

      <v-divider class="my-7"></v-divider>

      <v-row v-if="channelKey !== ''">
        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center">
          <v-label>ステータス</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 5">
          <v-chip :color="(isUsing) ? 'success' : ''" class="my-3">{{ isUsing ? '計測中' : '未使用' }}</v-chip>
        </v-col>

        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center">
          <v-label>入力ありの場合の表示</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 5">
          <v-text-field density="compact" counter
            :rules="isConfigurable ? [ v => (!!v) || '必須、10文字以内', v => (v && v.length <= 10) || '必須、10文字以内' ] : []"
            persistent-hint hint="必須、10文字以内"
            v-model="highMeaning" :readonly="!isConfigurable"></v-text-field>
        </v-col>

        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center">
          <v-label>入力なしの場合の表示</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 5">
          <v-text-field density="compact" counter
            :rules="isConfigurable ? [ v => (!!v) || '必須、10文字以内', v => (v && v.length <= 10) || '必須、10文字以内' ] : []"
            persistent-hint hint="必須、10文字以内"
            v-model="lowMeaning" :readonly="!isConfigurable"></v-text-field>
        </v-col>

        <v-col cols="12" class="my-2">
          <v-alert variant="tonal" type="success" v-model="isShowStartMessage" closable>
            データ保存を開始しました
          </v-alert>

          <v-alert variant="tonal" type="success" v-model="isShowUpdateMessage" closable>
            変更しました
          </v-alert>

          <v-alert variant="tonal" type="success" v-model="isShowDeleteMessage" closable>
            データ保存が停止されました
          </v-alert>
        </v-col>

        <v-col cols="12" class="d-flex justify-space-between" v-if="isConfigurable">
          <v-btn variant="outlined" color="error" v-if="!$vuetify.display.mobile" v-bind:class="{ 'v-hidden': !isUsing }"
          :loading="isDeleteLoading" v-on:click="isShowDialog = true;">設定を削除して計測を終了</v-btn>

          <v-btn :elevation="0" color="primary" :loading="isPutLoading" v-on:click="putInputMeaning">
            {{ isUsing ? '変更する' : '上記の設定で計測を開始' }}
          </v-btn>
        </v-col>

        <v-col cols="12" v-if="isConfigurable && $vuetify.display.mobile && isUsing">
          <v-btn variant="outlined" color="error" :loading="isDeleteLoading" v-on:click="isShowDialog = true;">
            設定を削除して計測を終了
          </v-btn>
        </v-col>
      </v-row>

    </v-form>

    <v-dialog max-width="400" v-model="isShowDialog">
      <v-card>
        <v-card-text>
          データ保存が停止されます。よろしいですか？
        </v-card-text>
        <v-card-actions class="d-flex justify-end">
          <v-btn v-on:click="isShowDialog = false;">
            キャンセル
          </v-btn>
          <v-btn color="error" v-on:click="isShowDialog = false; deleteInputMeaning();">
            削除
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { defineComponent, ref, onMounted, watch } from 'vue';

import axios from 'axios';

import config from '@/config.js';

export default defineComponent({
  name: 'DigitalInputUsageSetting',
  props: {
    moduleId: { type: String },
    isConfigurable: { type: Boolean }
  },

  setup(props) {

    const form = ref();

    const channelKey = ref('');
    const channels = ref([
      { title: '未選択', value: '' }
    ]);

    const isUsing = ref(false);

    const highMeaning = ref('');
    const lowMeaning = ref('');

    const isPutLoading = ref(false);
    const isDeleteLoading = ref(false);

    const isShowStartMessage = ref(false);
    const isShowUpdateMessage = ref(false);
    const isShowDeleteMessage = ref(false);

    const isShowDialog = ref(false);

    onMounted(() => {
      fetchChannelList();

      watch(channelKey, () => {
        highMeaning.value = '';
        lowMeaning.value = '';

        form.value.resetValidation();

        isShowStartMessage.value = false;
        isShowUpdateMessage.value = false;
        isShowDeleteMessage.value = false;

        if (channelKey.value === '') {
          return;
        }

        getInputMeaning();
      });
    });

    const fetchChannelList = () => {
      axios.get(
        config.API_URL +
        `/modules/${ props.moduleId }/channels`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem('access-token') } }
      ).then((response) => {

        if ('d1' in response.data.channels) {
          channels.value.push({
            title: '（デジタル入力1）' + response.data.channels.d1.name,
            value: '1'
          });
        }

        if ('d2' in response.data.channels) {
          channels.value.push({
            title: '（デジタル入力2）' + response.data.channels.d2.name,
            value: '2'
          });
        }

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });
    };

    const getInputMeaning = () => {
      axios.get(
        config.API_URL +
        `/modules/${ props.moduleId }/channels/digital/${ channelKey.value }`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem('access-token') } }
      ).then((response) => {

        if (response.data.highMeaning == null) {
          isUsing.value = false;
          return;
        }

        isUsing.value = true;

        highMeaning.value = response.data.highMeaning;
        lowMeaning.value = response.data.lowMeaning;

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });
    };

    const putInputMeaning = async () => {

      const { valid } = await form.value.validate();
      if (!valid) {
        return;
      }

      isPutLoading.value = true;

      isShowStartMessage.value = false;
      isShowUpdateMessage.value = false;
      isShowDeleteMessage.value = false;

      const reqBody = {
        highMeaning: highMeaning.value,
        lowMeaning: lowMeaning.value
      };

      axios.put(
        config.API_URL +
        `/modules/${ props.moduleId }/channels/digital/${ channelKey.value }`,
        reqBody,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem('access-token') } }
      ).then((/* response */) => {

        if (isUsing.value) {
          isShowUpdateMessage.value = true;
        } else {
          isShowStartMessage.value = true;
        }

        isUsing.value = true;

        isPutLoading.value = false;

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    };

    const deleteInputMeaning = () => {

      isDeleteLoading.value = true;

      isShowStartMessage.value = false;
      isShowUpdateMessage.value = false;

      axios.delete(
        config.API_URL +
        `/modules/${ props.moduleId }/channels/digital/${ channelKey.value }`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem('access-token') } }
      ).then((/* response */) => {

        isUsing.value = false;

        highMeaning.value = '';
        lowMeaning.value = '';

        form.value.resetValidation();

        isShowDeleteMessage.value = true;

        isDeleteLoading.value = false;

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    };

    return {
      form,

      channelKey,
      channels,

      isUsing,

      highMeaning,
      lowMeaning,

      isPutLoading,
      isDeleteLoading,

      isShowStartMessage,
      isShowUpdateMessage,
      isShowDeleteMessage,

      isShowDialog,

      putInputMeaning,
      deleteInputMeaning
    }

  }
});
</script>
